import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { API_URL } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import {
  formatOneDecimal,
  getErrorMessage,
  naturalCompare,
  parseApiErrorResponse,
} from '../utils/reviewHelpers';
import AdminDataTable, { AdminTableColumn as Column } from './AdminDataTable';
import AllocationMatrix, { AllocationBatchPayload } from './AllocationMatrix';

type Row = Record<string, any>;
type LoadStatus = 'idle' | 'loading' | 'success' | 'error';
type SectionKey =
  | 'companies'
  | 'glaciers'
  | 'assignments'
  | 'budgets'
  | 'overview'
  | 'progress'
  | 'readiness'
  | 'history';

interface SectionState {
  status: LoadStatus;
  error?: string;
}

const sectionKeys: SectionKey[] = [
  'companies',
  'glaciers',
  'assignments',
  'budgets',
  'overview',
  'progress',
  'readiness',
  'history',
];

const emptySectionState = (): Record<SectionKey, SectionState> =>
  sectionKeys.reduce((result, key) => {
    result[key] = { status: 'idle' };
    return result;
  }, {} as Record<SectionKey, SectionState>);

const isoToday = () => new Date().toISOString().slice(0, 10);
const sectionPaperSx = {
  p: 3,
  mt: 2,
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const request = async (path: string, options?: RequestInit) => {
  let response: Response;
  try {
    response = await authenticatedFetch(`${API_URL}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Network request failed'));
  }
  if (!response.ok) {
    throw new Error(await parseApiErrorResponse(response));
  }
  return response.status === 204 ? {} : response.json();
};

const summarizeChange = (event: Row) => {
  const before = event.before || {};
  const after = event.after || {};
  const ignored = new Set(['created_at', 'updated_at', 'changed_by']);
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
    .filter((key) => !ignored.has(key) && String(before[key]) !== String(after[key]));
  if (!keys.length) return 'Metadata updated';
  return keys.slice(0, 4).map((key) => {
    const oldValue = before[key] === null || before[key] === undefined ? '—' : String(before[key]);
    const newValue = after[key] === null || after[key] === undefined ? '—' : String(after[key]);
    return `${key}: ${oldValue} → ${newValue}`;
  }).join('; ');
};

const CommercialAdminPanelV2 = () => {
  const [companies, setCompanies] = useState<Row[]>([]);
  const [glaciers, setGlaciers] = useState<Row[]>([]);
  const [assignments, setAssignments] = useState<Row[]>([]);
  const [budgets, setBudgets] = useState<Row[]>([]);
  const [overview, setOverview] = useState<Row[]>([]);
  const [progress, setProgress] = useState<Row[]>([]);
  const [readiness, setReadiness] = useState<Row[]>([]);
  const [history, setHistory] = useState<Row[]>([]);
  const [latestEdits, setLatestEdits] = useState<Row[]>([]);
  const [sections, setSections] = useState(emptySectionState);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [company, setCompany] = useState({
    company_id: '',
    company_code: '',
    display_name: '',
    logo_uri: '',
  });
  const [budget, setBudget] = useState({
    company_id: '',
    season_year: 2026,
    target_m3: 0,
    effective_start: isoToday(),
  });

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    setSections((current) => sectionKeys.reduce((next, key) => ({
      ...next,
      [key]: { status: 'loading' as LoadStatus },
    }), { ...current }));

    const tasks: Array<{
      key: SectionKey;
      path: string;
      apply: (body: any) => void;
    }> = [
      { key: 'companies', path: '/admin/commercial/companies', apply: (body) => setCompanies(body.companies || []) },
      {
        key: 'glaciers',
        path: '/admin/commercial/glaciers',
        apply: (body) => {
          const rows = (body.glaciers || []).sort((a: Row, b: Row) =>
            naturalCompare(a.icestupa_id, b.icestupa_id));
          setGlaciers(rows);
        },
      },
      {
        key: 'assignments',
        path: '/admin/commercial/assignments',
        apply: (body) => setAssignments((body.assignments || []).sort((a: Row, b: Row) =>
          naturalCompare(a.company_id, b.company_id)
          || naturalCompare(a.icestupa_id, b.icestupa_id))),
      },
      { key: 'budgets', path: '/admin/commercial/budgets', apply: (body) => setBudgets(body.budgets || []) },
      {
        key: 'overview',
        path: '/admin/commercial/budget-overview',
        apply: (body) => setOverview((body.rows || []).sort((a: Row, b: Row) =>
          naturalCompare(a.icestupa_id, b.icestupa_id)
          || naturalCompare(a.company_code, b.company_code))),
      },
      { key: 'progress', path: '/admin/commercial/company-budget-progress', apply: (body) => setProgress(body.rows || []) },
      { key: 'readiness', path: '/admin/commercial/data-readiness', apply: (body) => setReadiness(body.checks || []) },
      {
        key: 'history',
        path: '/admin/commercial/change-history?limit=50',
        apply: (body) => {
          setHistory(body.events || []);
          setLatestEdits(body.latest || []);
        },
      },
    ];

    const results = await Promise.allSettled(tasks.map((task) => request(task.path)));
    const nextSections = emptySectionState();
    results.forEach((result, index) => {
      const task = tasks[index];
      if (result.status === 'fulfilled') {
        task.apply(result.value);
        nextSections[task.key] = { status: 'success' };
      } else {
        nextSections[task.key] = {
          status: 'error',
          error: getErrorMessage(result.reason, `Unable to load ${task.key}`),
        };
      }
    });
    setSections(nextSections);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = async (key: string, path: string, payload: Row, success: string) => {
    try {
      setPendingKey(key);
      setError(null);
      setMessage(null);
      await request(path, { method: 'PUT', body: JSON.stringify(payload) });
      setMessage(success);
      await refresh();
      return true;
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'Unable to save'));
      return false;
    } finally {
      setPendingKey(null);
    }
  };

  const deactivate = async (kind: 'company' | 'assignment', row: Row) => {
    const id = String(kind === 'company' ? row.company_id : row.assignment_id);
    const label = kind === 'company'
      ? row.display_name || row.company_name || row.company_code
      : `${row.icestupa_id} assignment`;
    if (!window.confirm(`Deactivate ${label}? This change will be recorded in the audit history.`)) return;
    try {
      setPendingKey(id);
      setError(null);
      setMessage(null);
      await request(
        `/admin/commercial/${kind === 'company' ? 'companies' : 'assignments'}/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      );
      setMessage(`${kind === 'company' ? 'Company' : 'Assignment'} deactivated.`);
      await refresh();
    } catch (removeError) {
      setError(getErrorMessage(removeError, `Unable to deactivate ${kind}`));
    } finally {
      setPendingKey(null);
    }
  };

  const saveAllocationMatrix = (payload: AllocationBatchPayload) =>
    save(
      'allocation-matrix',
      '/admin/commercial/assignments/batch',
      payload,
      'Glacier allocations saved.',
    );

  const latestByRecord = new Map(
    latestEdits.map((row) => [`${row.entity_type}:${row.entity_key}`, row]),
  );
  const withLatestEdit = (rows: Row[], entityType: string, keyFor: (row: Row) => string) =>
    rows.map((row) => {
      const latest = latestByRecord.get(`${entityType}:${keyFor(row)}`);
      return {
        ...row,
        last_action: latest?.action || null,
        last_changed_by: latest?.actor_subject || null,
        last_changed_at: latest?.changed_at || null,
      };
    });
  const companyRows = withLatestEdit(companies, 'company', (row) => String(row.company_id));
  const allocatableCompanies = useMemo(
    () => companies.filter(
      (row) => String(row.company_code || '').toLowerCase() !== 'nilus'
    ),
    [companies]
  );
  const assignmentRows = withLatestEdit(
    assignments,
    'glacier_assignment',
    (row) => String(row.assignment_id),
  );
  const budgetRows = withLatestEdit(
    budgets,
    'budget_target',
    (row) => `${row.company_id}:${row.season_year}:${String(row.effective_start).slice(0, 10)}`,
  );
  const historyRows = history.map((event) => ({
    changed_at: event.changed_at,
    editor: event.actor_subject,
    entity: event.entity_type,
    record: event.entity_key,
    action: event.action,
    source: event.source,
    changes: summarizeChange(event),
  }));

  const overviewColumns = useMemo<Column[]>(() => [
    { key: 'icestupa_id', label: 'Glacier ID' },
    { key: 'glacier_name', label: 'Glacier' },
    { key: 'company_code', label: 'Company code' },
    { key: 'company_name', label: 'Company' },
    { key: 'ownership_pct', label: 'Ownership %' },
    {
      key: 'replenished_m3',
      label: 'Replenished m³',
      displayValue: (row) => formatOneDecimal(row.replenished_m3),
    },
    {
      key: 'allocated_m3',
      label: 'Allocated m³',
      displayValue: (row) => formatOneDecimal(row.allocated_m3),
    },
  ], []);
  const progressColumns = useMemo<Column[]>(() => [
    { key: 'company_code', label: 'Company code' },
    { key: 'company_name', label: 'Company' },
    {
      key: 'current_allocated_m3',
      label: 'Current allocated m³',
      displayValue: (row) => formatOneDecimal(row.current_allocated_m3),
    },
    {
      key: 'target_m3',
      label: 'Target m³',
      displayValue: (row) => formatOneDecimal(row.target_m3),
    },
    {
      key: 'remaining_m3',
      label: 'Remaining m³',
      displayValue: (row) => formatOneDecimal(row.remaining_m3),
    },
    {
      key: 'percent_complete',
      label: 'Complete %',
      displayValue: (row) => row.percent_complete === null || row.percent_complete === undefined
        ? '—'
        : formatOneDecimal(row.percent_complete),
    },
    { key: 'as_of', label: 'As of' },
  ], []);

  return (
    <div>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <div>
          <Typography variant="h5" gutterBottom>Company Access and Budgets</Typography>
          <Typography color="text.secondary">
            Configure companies, glacier allocations, and current-season budget targets.
          </Typography>
        </div>
        <Button startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />} onClick={refresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ ...sectionPaperSx, mt: 0, height: '100%' }}>
            <Typography variant="h6">Company</Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField fullWidth label="Company code" value={company.company_code}
                  onChange={(event) => setCompany({ ...company, company_code: event.target.value.toLowerCase() })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Display name" value={company.display_name}
                  onChange={(event) => setCompany({ ...company, display_name: event.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Logo URI" value={company.logo_uri}
                  onChange={(event) => setCompany({ ...company, logo_uri: event.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  disabled={pendingKey === 'company-form' || !company.company_code || !company.display_name}
                  onClick={() => save(
                    'company-form',
                    '/admin/commercial/companies',
                    {
                      ...company,
                      company_id: company.company_id || undefined,
                      status: 'active',
                      logo_uri: company.logo_uri || null,
                    },
                    'Company saved.',
                  )}
                >
                  {pendingKey === 'company-form' ? 'Saving…' : 'Save company'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ ...sectionPaperSx, mt: 0, height: '100%' }}>
            <Typography variant="h6">Budget target</Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField select fullWidth label="Company" value={budget.company_id}
                  onChange={(event) => setBudget({ ...budget, company_id: event.target.value })}>
                  {allocatableCompanies.map((item) => (
                    <MenuItem key={item.company_id} value={item.company_id}>
                      {item.display_name || item.company_name || item.company_code}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth type="number" label="Season" value={budget.season_year}
                  onChange={(event) => setBudget({ ...budget, season_year: Number(event.target.value) })} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth type="number" label="Target m³" value={budget.target_m3}
                  onChange={(event) => setBudget({ ...budget, target_m3: Number(event.target.value) })} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth type="date" label="Effective start" value={budget.effective_start}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setBudget({ ...budget, effective_start: event.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  disabled={pendingKey === 'budget-form' || !budget.company_id}
                  onClick={() => save(
                    'budget-form',
                    '/admin/commercial/budgets',
                    budget,
                    'Budget target saved.',
                  )}
                >
                  {pendingKey === 'budget-form' ? 'Saving…' : 'Save budget'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={sectionPaperSx}>
        <AllocationMatrix
          companies={allocatableCompanies}
          glaciers={glaciers}
          assignments={assignments}
          loading={
            ['idle', 'loading'].includes(sections.companies.status)
            || ['idle', 'loading'].includes(sections.glaciers.status)
            || ['idle', 'loading'].includes(sections.assignments.status)
          }
          loadError={
            sections.companies.error
            || sections.glaciers.error
            || sections.assignments.error
          }
          saving={pendingKey === 'allocation-matrix'}
          onSave={saveAllocationMatrix}
        />
      </Paper>

      <Paper sx={sectionPaperSx}>
        <Typography variant="h6" gutterBottom>Allocation overview</Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          Per-glacier allocation details; company targets are shown only above.
        </Typography>
        <AdminDataTable name="Allocation overview" rows={overview} state={sections.overview} columns={overviewColumns} />
      </Paper>

      <Paper sx={sectionPaperSx}>
        <Typography variant="h6" gutterBottom>Data readiness</Typography>
        <AdminDataTable name="Data readiness" rows={readiness} state={sections.readiness} />
      </Paper>
      <Paper sx={sectionPaperSx}>
        <Typography variant="h6" gutterBottom>Companies</Typography>
        <AdminDataTable
          name="Companies"
          rows={companyRows}
          state={sections.companies}
          pendingKey={pendingKey}
          onEdit={(row) => setCompany({
            company_id: row.company_id || '',
            company_code: row.company_code || '',
            display_name: row.display_name || row.company_name || '',
            logo_uri: row.logo_uri || '',
          })}
          onDeactivate={(row) => deactivate('company', row)}
        />
      </Paper>
      <Paper sx={sectionPaperSx}>
        <Typography variant="h6" gutterBottom>Assignment records</Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          Current assignment IDs, effective dates, and audit metadata.
        </Typography>
        <AdminDataTable
          name="Assignment records"
          rows={assignmentRows}
          state={sections.assignments}
        />
      </Paper>
      <Paper sx={sectionPaperSx}>
        <Typography variant="h6" gutterBottom>Budget targets</Typography>
        <AdminDataTable name="Budget targets" rows={budgetRows} state={sections.budgets} />
      </Paper>
      <Paper sx={sectionPaperSx}>
        <Typography variant="h6" gutterBottom>Company budget progress</Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          One current-season allocated-versus-target row per company.
        </Typography>
        <AdminDataTable name="Company budget progress" rows={progress} state={sections.progress} columns={progressColumns} />
      </Paper>
      <Paper sx={sectionPaperSx}>
        <Typography variant="h6" gutterBottom>Recent configuration changes</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Append-only history from Databricks. Restore actions are intentionally not exposed.
        </Typography>
        <AdminDataTable name="Recent configuration changes" rows={historyRows} state={sections.history} />
      </Paper>
    </div>
  );
};

export default CommercialAdminPanelV2;
